import { useMemo, useState } from "react"
import {
    Box,
    Typography,
    List,
    ListItemButton,
    ListItemText,
    Collapse,
    Divider,
    Modal,
    Button,
} from "@mui/material"
import CircularProgress from '@mui/material/CircularProgress';

import RemoveIcon from '@mui/icons-material/Remove';
import Add from "@mui/icons-material/Add"
import CheckCircle from "@mui/icons-material/CheckCircle"
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked"
import { DocumentLabel, DocumentSection } from "../utils/constants"


interface DocumentConfig {
    label: DocumentLabel
    mandatory: boolean
    uploaded: boolean
    section: DocumentSection
}

interface Props {
    documents: Record<string, DocumentConfig>
    onSelect: (key: DocumentConfig) => void
    loading?: boolean
    uploadedCorrectBankStatements?: boolean

}

export default function FinancialSidebar({
    documents,
    onSelect,
    loading,
    uploadedCorrectBankStatements
}: Props) {

    const [openSections, setOpenSections] = useState<Record<string, boolean>>({
        "COMPANY FINANCIAL DOCUMENTS": true
    })
    const [activeDocument, setActiveDocument] = useState<keyof typeof documents | null>('BANK_STATEMENTS')
    const [openModal, setOpenModal] = useState<any>(false)

    // ---- GROUP DOCUMENTS BY SECTION ----
    const groupedSections = useMemo(() => {
        return Object.entries(documents).reduce((acc, [key, value]) => {
            if (!acc[value.section]) {
                acc[value.section] = []
            }
            acc[value.section].push({ key, ...value })
            return acc
        }, {} as Record<DocumentSection, Array<{ key: string } & DocumentConfig>>)
    }, [documents])

    // ---- GLOBAL PROGRESS ----
    const totalDocs = Object.values(documents).length
    const mandatoryDocs = Object.values(documents).filter(d => d.mandatory)
    const uploadedDocs = Object.values(documents).filter(d => d.uploaded)

    const progress =
        mandatoryDocs.length === 0
            ? 0
            : (uploadedDocs.length / totalDocs) * 100

    const toggleSection = (section: DocumentSection) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }))
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: "calc(100vh - 310px)",
                border: "1px solid #e0e0e0",
                display: "flex",
                flexDirection: "column",
                borderRadius: '8px'
            }}
        >
            {/* -------- Progress Section -------- */}
            <Box sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Box position="relative">
                        {/* The Track (Background) */}
                        <CircularProgress
                            variant="determinate"
                            sx={{ color: (theme) => theme.palette.grey[200] }}
                            size={52}
                            thickness={4}
                            value={100}
                        />
                        {/* The Active Progress */}
                        <CircularProgress
                            variant="determinate"
                            value={progress}
                            size={52}
                            thickness={4}
                            sx={{
                                color: '#013238',
                                position: 'absolute',
                                left: 0,
                            }}
                        />
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            bottom={0}
                            right={0}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                        </Box>
                    </Box>

                    <Box>
                        <Typography fontSize={18} fontWeight={600} >
                            {uploadedDocs.length} of {totalDocs} Documents Uploaded
                        </Typography>
                        <Typography fontSize={16} color="text.secondary">
                            Your upload progress will be updated
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider />

            {/* -------- Sections -------- */}
            <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
                {Object.entries(groupedSections).map(([section, items]) => {

                    const mandatory = items.filter(d => d.mandatory)
                    const uploaded = mandatory.filter(d => d.uploaded)

                    const isSectionComplete =
                        mandatory.length > 0 &&
                        uploaded.length === mandatory.length

                    return (
                        <Box key={section} mb={2}>
                            {/* Section Header */}
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ cursor: "pointer" }}
                                onClick={() => {
                                    if (!loading) {
                                        toggleSection(section as DocumentSection)
                                    }
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1}>

                                    <Typography
                                        variant="caption"
                                        fontWeight={500}
                                        color="#616B75"
                                        fontSize={'14px'}
                                    >
                                        {section}
                                    </Typography>
                                </Box>

                                {openSections[section] ? (
                                    <RemoveIcon fontSize="small" />
                                ) : (
                                    <Add fontSize="small" />
                                )}
                            </Box>

                            {/* Section Documents */}
                            <Collapse in={openSections[section]}>
                                <List disablePadding sx={{ mt: 1 }}>
                                    {items.map(doc => (
                                        <ListItemButton
                                            key={doc.key}
                                            onClick={() => {
                                                if (!loading) {
                                                    const bankStatementAccountDetails = window.localStorage.getItem('accountDetails')

                                                    if (activeDocument === 'BANK_STATEMENTS' && (!uploadedCorrectBankStatements && bankStatementAccountDetails !== '[]')) {
                                                        setOpenModal({open: true, clickedDocument: doc})
                                                        return
                                                    }
                                                    onSelect(doc);
                                                    setActiveDocument(doc.key);
                                                }
                                            }}
                                            sx={{
                                                borderRadius: 1,
                                                mb: 0.5,
                                                backgroundColor: activeDocument === doc.key ? "#F0F3F4" : "transparent"
                                            }}
                                        >
                                            {doc.uploaded ? (
                                                <CheckCircle
                                                    fontSize="small"
                                                    sx={{
                                                        color: '#013238'
                                                    }}
                                                />
                                            ) : (
                                                <RadioButtonUnchecked
                                                    color="disabled"
                                                    fontSize="small"
                                                />
                                            )}
                                            <ListItemText
                                                primary={doc.label}
                                                primaryTypographyProps={{
                                                    fontSize: 14
                                                }}
                                                sx={{
                                                    paddingLeft: 1,
                                                    marginTop: 1
                                                }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Collapse>
                        </Box>
                    )
                })}
            </Box>
            {openModal.open &&
                <Modal
                    open={openModal.open}
                    onClose={() => setOpenModal({open: false, clickedDocument: null})}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            bgcolor: 'background.paper',
                            borderRadius: '8px',
                            boxShadow: 24,
                            p: 4,
                        }}
                    >
                        <Typography id="modal-modal-title" variant="h6" component="h2">
                            You have not uploaded all 6 month bank statements. If you leave this page,
                            all progress will be lost. Continue?
                        </Typography>

                        {/* Buttons */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 2,
                                mt: 3,
                            }}
                        >
                            <button
                                className="button button-outline"
                                style={{ fontSize:'12px' }}
                                onClick={() => setOpenModal({open: false, clickedDocument: null})}
                            >
                                Cancel
                            </button>

                            <button
                                className="button button-primary"
                                style={{ fontSize:'12px' }}
                                onClick={() => {
                                    onSelect(openModal.clickedDocument);
                                    setActiveDocument(openModal.clickedDocument.key);
                                    setOpenModal({open: false, clickedDocument: null});
                                }}
                            >
                                Continue
                            </button>
                        </Box>
                    </Box>
                </Modal>}
        </Box>
    )
}
